import { BaseViewStore } from 'core/stores';
import { FulfillmentOrderProcessingStore, FulfillmentOrderStore } from 'modules/order/stores';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { ChangeShippingForm } from 'modules/order/forms';
import { ApplicationStore } from 'platform/application/stores';
import {
	notificationError,
	getDistinctProductIds,
	mapCarrierServiceWithRates,
	translate,
	shouldUseBuyShipping,
	getBulkItemsSellerId,
	isSuperAdminOrganization,
} from 'core/utils';
import { action, makeObservable, observable } from 'mobx';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { RateServiceStore } from 'modules/rate/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { channelType, onChangeShippingStatuses, shipmentInfoStatusEnum, sourceOfChange } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';

class ChangeShippingViewStore extends BaseViewStore {
	activeShipment = null;
	isBulkAction = false;
	isAllAmazon = false;
	selectedItem = null;
	@observable isFormDisabled = true;

	form = new ChangeShippingForm({
		onSuccess: async () => {
			if (this.selectedItems != null) {
				await this.changeShippingBulk();
			} else {
				await this.changeShipping();
			}
		},
	});

	getVoidLabelConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				if (this.selectedItems == null) await this.changeShipping(1);
				else await this.changeShippingBulk(1);
			},
			onCancel: async () => {
				if (this.selectedItems == null) await this.changeShipping(0);
				else await this.changeShippingBulk(0);
			},
		};
	};

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize(_.get(this.modalParams, 'data.selectedItemId'), _.get(this.modalParams, 'data.selectedItemIds'));
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		//this is temporary
		if (selectedItemIds != null) {
			this.isBulkAction = true;
			await this.findFulfillmentOrder({
				ids: selectedItemIds,
				embed: [
					'order',
					'order.channel',
					'order.channel.channelType',
					'fulfillmentOrderStatus',
					'fulfillmentOrderItems',
					'fulfillmentOrderItems.orderLine',
					'fulfillmentOrderItems.product',
					'shipmentInfos',
				],
			});
			this.filteredItems = _.filter(this.selectedItems, selectedItem => {
				let index = _.findIndex(onChangeShippingStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					return selectedItem;
				}
			});
			this.isAllAmazon =
				_.find(this.filteredItems, item => item.order.channel.channelType.name !== channelType.amazon) === undefined
					? true
					: false;
			this.form.updateForm({
				useBuyShipping: false,
				ignoreDeliverByDate: this.isAllAmazon ? !this.useBuyShipping : true,
			});
		} else {
			await this.findFulfillmentOrder({
				ids: [selectedItemId],
				embed: [
					'order',
					'order.channel',
					'order.channel.channelType',
					'order.shippingCoreCountry',
					'order.shippingCoreCountryState',
					'carrierService',
					'carrier',
					'fulfillmentOrderItems',
					'fulfillmentOrderItems.product',
					'warehouseOrganization',
					'shipmentInfos',
				],
			});
			let useBuyShipping = shouldUseBuyShipping(this.selectedItem, this.form);
			this.form.updateForm({
				useBuyShipping: useBuyShipping,
				ignoreDeliverByDate:
					_.get(this.selectedItem, 'order.channel.channelType.name') === channelType.amazon ? !useBuyShipping : true,
			});
		}
		await this.initializeDropdowns();
		this.resume();
	};

	@action.bound
	findFulfillmentOrder = async filter => {
		const data = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			...filter,
		})).item;
		if (this.isBulkAction) {
			this.selectedItems = data;
		} else {
			this.selectedItem = data.pop();
			this.activeShipment = _.find(this.selectedItem.shipmentInfos, item => {
				return item.status == shipmentInfoStatusEnum.active;
			});
		}
	};

	@action.bound
	initializeDropdowns = async () => {
		this.warehouseDropdownStore = new BaasicDropdownViewStore();
		await this.warehouseDropdownStore.initialize({
			field: this.form.$('warehouseId'),
			...this.getWarehouseDropdownProps(),
		});
		this.carrierServiceDropdownStore = new BaasicDropdownViewStore();
		await this.carrierServiceDropdownStore.initialize({
			field: this.form.$('carrierServiceId'),
			...this.getCarrierServiceDropdownProps(),
		});
		if (!this.isBulkAction) {
			this.applyCarrierServiceOnWarehouseChange();
			this.checkAndNotifyForAvailableWarehouses();
		}
	};

	getCarrierServiceDropdownProps = () => {
		if (this.isBulkAction) {
			return {
				store: new CarrierServiceStore(this.rootStore),
				filterProperties: {
					embed: ['carrier'],
				},
			};
		} else {
			return {
				store: new RateServiceStore(this.rootStore),
				value: {
					...this.selectedItem.carrierService,
					carrier: {
						id: this.selectedItem.assignedCarrierId,
					},
				},
				filterProperties: {
					fulfillmentOrderId: this.selectedItem.id,
					ignoreDeliverByDate: this.isSuperAdminOrganization ? this.form.$('ignoreDeliverByDate').value : true,
					warehouseId: this.form.$('warehouseId').value,
					useBuyShipping: this.form.$('useBuyShipping').value,
				},
				fetchAll: true,
				fetchFunc: 'getRates',
				filterCallback: mapCarrierServiceWithRates,
				onChangeCallback: () => {
					this.isFormDisabled = !this.hasShippingDataChanged();
				},
			};
		}
	};

	getWarehouseDropdownProps = () => {
		if (this.isBulkAction) {
			return {
				store: new ApplicationStore(this.rootStore),
				filterProperties: {
					isOrganizationActive: true,
					warehouseOnly: true,
					includeOnlySellerWarehouses: true,
					sellerWarehousesOnlyOrganizationId: getBulkItemsSellerId(this.selectedItems),
				},
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
			};
		} else {
			return {
				store: new ProductInventoryStore(this.rootStore),
				value: {
					value: this.selectedItem.warehouseOrganization.id,
					label: this.selectedItem.warehouseOrganization.applicationTitle,
					extra: this.selectedItem.warehouseOrganization,
				},
				itemTemplate: {
					value: 'assignedWarehouseId',
					label: 'assignedWarehouse.applicationTitle',
				},
				filterProperties: {
					productIds: getDistinctProductIds(this.selectedItem.fulfillmentOrderItems),
					fulfillmentOrderId: this.selectedItem.id,
					calculateInventory: true,
					filterInapplicableWarehouses: true,
					includeOnlySellerWarehouses: true,
					sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
					isOrganizationActive: true,
					useSafeQuantity: false,
					embed: ['assignedWarehouse', 'product', 'product.multiPackageProductParts'],
				},
				onChangeCallback: async selectedOption => {
					this.suspend();
					await this.carrierServiceDropdownStore.updateFilter({
						fulfillmentOrderId: this.selectedItem.id,
						warehouseId: selectedOption.value,
						ignoreDeliverByDate: this.form.$('ignoreDeliverByDate').value,
						useBuyShipping: this.form.$('useBuyShipping').value,
					});
					this.applyCarrierServiceOnWarehouseChange();
					this.isFormDisabled = !this.hasShippingDataChanged();
					this.resume();
				},
			};
		}
	};

	@action.bound
	applyCarrierServiceOnWarehouseChange = () => {
		let matchingCarrierService = this.carrierServiceDropdownStore.value
			? this.carrierServiceDropdownStore.options.find(
					o =>
						o.value === this.carrierServiceDropdownStore.value.value &&
						_.get(o, 'extra.carrier.id') == _.get(this.carrierServiceDropdownStore.value, 'extra.carrier.id')
			  )
			: null;
		if (matchingCarrierService) {
			this.carrierServiceDropdownStore.setValue(matchingCarrierService);
		} else {
			this.carrierServiceDropdownStore.clear();
		}
	};

	@action.bound
	checkAndNotifyForAvailableWarehouses = () => {
		if (
			(this.warehouseDropdownStore.options.length == 0 ||
				(this.warehouseDropdownStore.options.length == 1 &&
					_.get(this.warehouseDropdownStore.options[0], 'value') == this.selectedItem.assignedWarehouseId)) &&
			(this.carrierServiceDropdownStore.options.length == 0 ||
				(this.carrierServiceDropdownStore.options.length == 1 &&
					_.get(this.carrierServiceDropdownStore.options[0], 'value') == this.selectedItem.assignedCarrierServiceId))
		) {
			this.rootStore.notificationStore.error('CHANGESHIPPINGVIEW.NOELIGIBLESHIPPINGDATA');
			this.isFormDisabled = true;
		}
	};

	@action.bound
	openVoidActiveLabelConfirmModal() {
		if (this.isBulkAction) {
			for (const selectedItem of this.selectedItems) {
				const activeShipment = _.find(selectedItem.shipmentInfos, item => {
					return item.status == shipmentInfoStatusEnum.active;
				});
				if (activeShipment != null) {
					var hasActiveShipments = true;
					var hasActiveShipmentsWithBuyShipping = activeShipment.isBuyShipping == true;
					if (hasActiveShipmentsWithBuyShipping == true) break;
				}
			}
		}
		if (this.activeShipment != null || hasActiveShipments) {
			const message =
				hasActiveShipmentsWithBuyShipping || (!this.isBulkAction && this.activeShipment.isBuyShipping)
					? this.isBulkAction
						? 'CHANGECARRIERVIEW.MODAL.BULKVOIDLABEL'
						: ['CHANGECARRIERVIEW.MODAL.VOIDLABEL', { fulfillmentOrderId: this.selectedItem.fulfillmentOrderId }]
					: null;
			if (message == null) {
				this.isBulkAction ? this.changeShippingBulk(0) : this.changeShipping(0);
			} else {
				this.openConfirmationModal({ ...this.getVoidLabelConfirmModalProps(), message });
			}
		} else {
			if (this.selectedItems != null) {
				this.changeShippingBulk(3);
			} else {
				this.changeShipping(3);
			}
		}
	}

	@action.bound
	onIgnoreDeliverByDateChange = async e => {
		if (this.isBulkAction || _.get(this.selectedItem, 'order.channel.channelType.name') === channelType.amazon) {
			this.form.$('useBuyShipping').value = !e.currentTarget.checked;
		}
		if (!this.isBulkAction && !this.form.$('warehouseId').isEmpty) {
			this.suspend();
			this.carrierServiceDropdownStore.clear();
			await this.carrierServiceDropdownStore.updateFilter({
				ignoreDeliverByDate: e.currentTarget.checked,
			});
			this.resume();
		}
	};

	@action.bound
	changeShipping = async voidOption => {
		this.suspend();
		try {
			const { voidLabelOptionId, ignoreDeliverByDate, ...otherValues } = this.form.values();
			const changeShippingParams = {
				id: this.selectedItem.id,
				voidOption: voidOption,
				rate: this.carrierServiceDropdownStore.value.extra.rate,
				surcharge: this.carrierServiceDropdownStore.value.extra.rateComposition.surcharge,
				carrierId: _.get(this.carrierServiceDropdownStore.value, 'extra.carrier.id'),
				...otherValues,
			};
			await new FulfillmentOrderProcessingStore(this.rootStore).changeShipping(
				changeShippingParams,
				`${sourceOfChange.changeShippingOrder} - ${sourceOfChange.voidLabelOption[voidOption]}`
			);
			this.rootStore.notificationStore.success([
				'CHANGESHIPPINGVIEW.SUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
			if (voidOption !== 3 && this.modalParams.data.resetPrintingData) {
				this.modalParams.data.resetPrintingData();
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['CHANGESHIPPINGVIEW.FAIL', { id: this.selectedItem.fulfillmentOrderId }]);
		}
		this.modalParams.success();
		this.resume();
	};

	@action.bound
	changeShippingBulk = async voidOption => {
		try {
			const itemIds = [];
			const notProcessableItems = [];
			_.each(this.selectedItems, selectedItem => {
				const index = _.findIndex(onChangeShippingStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index == -1 || _.get(selectedItem, 'order.channel.channelType.name') === channelType.amazonVendor) {
					notProcessableItems.push(selectedItem);
				} else {
					itemIds.push(selectedItem.id);
				}
			});
			if (this.selectedItems.length > 0) {
				this.modalParams.close();
				const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.CHANGESHIPPING'));
				const count = await new FulfillmentOrderProcessingStore(this.rootStore).changeShippingBulk(
					{
						ids: itemIds,
						voidOption: voidOption,
						...this.form.values(),
					},
					`${sourceOfChange.changeShippingOrder} - ${sourceOfChange.bulk} - ${sourceOfChange.voidLabelOption[voidOption]}`
				);
				this.rootStore.notificationStore.dismiss(notificationId);
				if (count > 0) {
					const messageParams = {
						count: count,
						countMax: itemIds.length,
						notProcessable: notProcessableItems.length,
					};
					if (count == itemIds.length) {
						this.rootStore.notificationStore.success(['CHANGESHIPPINGVIEW.SUCCESSBULK', messageParams]);
					} else {
						this.rootStore.notificationStore.success(['CHANGESHIPPINGVIEW.PARTIALSUCCESSBULK', messageParams]);
					}
				} else {
					this.rootStore.notificationStore.error('CHANGESHIPPINGVIEW.NOVALIDORDERSELECTED');
				}
			} else {
				this.rootStore.notificationStore.error('CHANGESHIPPINGVIEW.NOVALIDORDERSELECTED');
			}
			if (voidOption !== 3 && this.modalParams.data.resetPrintingData) {
				this.modalParams.data.resetPrintingData();
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.modalParams.success();
	};

	hasShippingDataChanged = () => {
		const { warehouseId, carrierServiceId, useBuyShipping } = this.form.values();
		return (
			warehouseId != this.selectedItem.assignedWarehouseId ||
			(carrierServiceId != this.selectedItem.assignedCarrierServiceId ||
				this.carrierServiceDropdownStore.value.extra.carrier.id != this.selectedItem.assignedCarrierId ||
				useBuyShipping != this.selectedItem.useBuyShipping)
		);
	};
}

export default ChangeShippingViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/ChangeShippingViewStore.js